import { useEffect } from 'react';

import { setPhiBlurSource } from '@/utils/phiBlur';

/**
 * How long PHI stays blurred after the chord is released and the window has focus again. Covers a
 * capture that is still finishing as focus comes back, so the blur doesn't flicker off mid-shot.
 */
export const SCREENSHOT_UNBLUR_GRACE_MS = 300;

/**
 * Blurs `.mp-sensitive` elements as soon as a screenshot chord could be starting.
 *
 * Nobody presses a screenshot chord at once: the modifiers go down first and the OS only
 * intercepts the final key. macOS Cmd+Shift+3/4/5 and Windows Win+Shift+S both start with Meta and
 * Shift held, which the page still sees, so we blur then. PrtScn is a single key the page may only
 * see on keyup, so it is best effort.
 *
 * The listeners are native and in the capture phase and the class goes straight onto <html>, so
 * the blur paints on the next frame rather than waiting for React.
 *
 * Unblurring waits for both modifiers to be up, the window to have focus again (Cmd+Shift+4/5 and
 * the Snipping Tool overlay take focus while capturing), and a short grace delay.
 *
 * Cmd+Shift is also part of ordinary shortcuts (Cmd+Shift+T, Cmd+Shift+Z), so PHI blurs briefly
 * then too. That flicker is the accepted price of catching the screenshot before it happens.
 */
export default function useScreenshotPhiBlur() {
  useEffect(() => {
    let metaHeld = false;
    let shiftHeld = false;
    let windowFocused = document.hasFocus();
    let blurred = false;
    let unblurTimer: ReturnType<typeof setTimeout> | undefined;

    const cancelUnblur = () => {
      if (unblurTimer !== undefined) {
        clearTimeout(unblurTimer);
        unblurTimer = undefined;
      }
    };

    const blur = () => {
      cancelUnblur();
      if (blurred) return;
      blurred = true;
      setPhiBlurSource('precursor', true);
    };

    const scheduleUnblur = () => {
      // Any pending unblur restarts from here, so a modifier pressed again during the grace
      // period keeps the blur rather than letting the earlier timer clear it.
      cancelUnblur();
      if (!blurred || metaHeld || shiftHeld || !windowFocused) return;

      unblurTimer = setTimeout(() => {
        unblurTimer = undefined;
        blurred = false;
        setPhiBlurSource('precursor', false);
      }, SCREENSHOT_UNBLUR_GRACE_MS);
    };

    const trackModifiers = (event: KeyboardEvent) => {
      metaHeld = event.metaKey;
      shiftHeld = event.shiftKey;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      trackModifiers(event);

      if (metaHeld && shiftHeld) {
        blur();
      } else {
        scheduleUnblur();
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      trackModifiers(event);

      if (event.key === 'PrintScreen') blur();

      scheduleUnblur();
    };

    const handleWindowBlur = () => {
      windowFocused = false;

      // A capture overlay or the Cmd+Shift+5 toolbar has taken focus.
      if (metaHeld && shiftHeld) blur();

      // Hold the blur while we are away. Pending unblurs wait for focus to come back.
      cancelUnblur();

      // Keyups land in the capture overlay while we are unfocused, so the held state would go
      // stale. Unblurring still waits for focus to return, which is what protects the capture.
      metaHeld = false;
      shiftHeld = false;
    };

    const handleWindowFocus = () => {
      windowFocused = true;
      scheduleUnblur();
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      cancelUnblur();
      if (blurred) setPhiBlurSource('precursor', false);
    };
  }, []);
}
